import {
  Alert,
  Box,
  Button,
  ButtonProps,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  styled,
  SvgIconProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import { SxProps } from "@mui/system";
import {
  UiContainer,
  UiNode,
  UiNodeImageAttributes,
  UiNodeInputAttributes,
  UiNodeTextAttributes,
  UiText,
} from "@ory/kratos-client";
import useMemoizedFn from "ahooks/es/useMemoizedFn";
import React, { CSSProperties, useEffect, useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  Path,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { GoogleIcon, MicrosoftIcon } from "../../pages/LoginPage/icons";
import { PasswordField } from "../form";
import { getInitialFormState } from "./form";
import { filterNodes, getNodeLabel, getTranslatedNodeLabel } from "./ui";

interface UiNodeInputProps {
  node: UiNode;
  attributes: UiNodeInputAttributes;
  hideAttributes?: string[];
}

interface UiNodeButtonProps extends UiNodeInputProps {
  addDiveder?: boolean;
  fullWidth?: boolean;
  leftElement?: React.ReactNode;
  buttonText?: string;
  buttonIcon?: React.ReactElement<SvgIconProps>;
  cancelFormButton?: React.ReactNode;
  buttonVariant?: "text" | "outlined" | "contained";
}

const UiNodeInputCheckboxComponent: React.FC<UiNodeInputProps> = ({
  node,
  attributes,
}) => {
  const { t } = useTranslation("kratos");
  const { control } = useFormContext();
  return (
    <Controller
      name={attributes.name}
      control={control}
      defaultValue={attributes.value}
      render={({ field, fieldState, formState }) => (
        <FormControl
          error={!!fieldState.error}
          disabled={attributes.disabled || formState.isSubmitting}
        >
          <FormControlLabel
            control={<Checkbox {...field} checked={field.value} />}
            label={getTranslatedNodeLabel(node, t)}
          />
        </FormControl>
      )}
    />
  );
};

const UiNodeInputDefaultComponent: React.FC<UiNodeInputProps> = ({
  node,
  attributes,
  hideAttributes,
}) => {
  const { t } = useTranslation("kratos");
  const { control, setValue } = useFormContext();
  const location = useLocation();
  const [isFieldDisabled, setIsFieldDisabled] = useState(false);

  const TextFieldComponent =
    attributes.type === "password" ? PasswordField : TextField;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailValue = searchParams.get("email");
    const invitationCodeValue = searchParams.get("invitationCode");

    let shouldBeDisabled = false;

    if (emailValue && attributes.name === "traits.email") {
      setValue("traits.email", emailValue, { shouldValidate: true });
      shouldBeDisabled = true;
    }
    if (invitationCodeValue && attributes.name === "traits.invitation_code") {
      setValue("traits.invitation_code", invitationCodeValue, {
        shouldValidate: true,
      });
      shouldBeDisabled = true;
    }
    setIsFieldDisabled(shouldBeDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, location.search]);

  return (
    <Controller
      name={attributes.name}
      control={control}
      defaultValue={attributes.value}
      render={({ field, fieldState, formState }) =>
        !hideAttributes?.includes(attributes.name) ? (
          <>
            <TextFieldComponent
              fullWidth
              required={attributes.name !== "code" && attributes.required}
              disabled={
                isFieldDisabled || attributes.disabled || formState.isSubmitting
              }
              type={attributes.type}
              {...field}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              placeholder={getTranslatedNodeLabel(node, t)}
            />
            {attributes.type === "password" &&
              location.pathname === "/register" && (
                <StyledTypography fontSize="small">
                  Minimum 8 characters, 1 uppercase letter and 1 number
                </StyledTypography>
              )}
          </>
        ) : (
          <></>
        )
      }
    />
  );
};

const UiNodeInputHiddenComponent: React.FC<UiNodeInputProps> = ({
  attributes,
}) => <input type="hidden" name={attributes.name} value={attributes.value} />;

const UiNodeInputButtonComponent: React.FC<UiNodeButtonProps> = ({
  node,
  attributes,
  addDiveder,
  fullWidth,
  leftElement,
  buttonText,
  buttonIcon,
  cancelFormButton,
  buttonVariant,
}) => {
  if (node.group === "oidc" || node.meta.label?.id === 1070008) {
    return (
      <UiNodeInputButtonOidcComponent node={node} attributes={attributes} />
    );
  } else {
    return (
      <UiNodeInputButtonPlainComponent
        node={node}
        attributes={attributes}
        addDiveder={addDiveder}
        fullWidth={fullWidth}
        leftElement={leftElement}
        buttonText={buttonText}
        buttonIcon={buttonIcon}
        cancelFormButton={cancelFormButton}
        buttonVariant={buttonVariant}
      />
    );
  }
};

const UiNodeInputButtonPlainComponent: React.FC<UiNodeButtonProps> = ({
  node,
  attributes,
  fullWidth = true,
  addDiveder = false,
  leftElement,
  buttonText,
  buttonIcon,
  cancelFormButton,
  buttonVariant,
}) => {
  const { t } = useTranslation("kratos");
  const { formState } = useFormContext();

  return (
    <>
      {addDiveder && <Divider sx={{ marginTop: "24px" }} />}
      {fullWidth ? (
        <Button
          variant={buttonVariant || "contained"}
          fullWidth
          name={attributes.name}
          value={attributes.value}
          disabled={
            attributes.disabled || !formState.isValid || formState.isSubmitting
          }
          type={attributes.type as unknown as ButtonProps["type"]}
          endIcon={buttonIcon}
        >
          {buttonText ? buttonText : getTranslatedNodeLabel(node, t)}
        </Button>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={cancelFormButton ? 2 : 4}>
            {leftElement}
          </Grid>
          {cancelFormButton && (
            <Grid item xs={5}>
              {cancelFormButton}
            </Grid>
          )}
          <Grid item xs={cancelFormButton ? 5 : 8}>
            <Button
              variant={buttonVariant || "contained"}
              fullWidth
              name={attributes.name}
              value={attributes.value}
              disabled={
                attributes.disabled ||
                !formState.isValid ||
                formState.isSubmitting
              }
              type={attributes.type as unknown as ButtonProps["type"]}
              endIcon={buttonIcon}
            >
              {buttonText ? buttonText : getTranslatedNodeLabel(node, t)}
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};

const UiNodeInputButtonOidcComponent: React.FC<UiNodeInputProps> = ({
  node,
  attributes,
}) => {
  const { t } = useTranslation("kratos");
  const { formState } = useFormContext();

  return (
    <Button
      variant={"outlined"}
      fullWidth
      name={attributes.name}
      value={attributes.value}
      disabled={attributes.disabled || formState.isSubmitting}
      type={attributes.type as any}
      startIcon={
        attributes.value === "google" ? (
          <GoogleIcon />
        ) : attributes.value === "microsoft" ? (
          <MicrosoftIcon />
        ) : undefined
      }
      sx={{ color: "black" }}
    >
      {getTranslatedNodeLabel(node, t)}
    </Button>
  );
};

const UiNodeInputComponent: React.FC<{
  node: UiNode;
  attributes: UiNodeInputAttributes;
  hideAttributes?: string[];
  useDivider?: boolean;
  fullWidthButton?: boolean;
  elementNextToButton?: React.ReactNode;
  buttonText?: string;
  buttonIcon?: React.ReactElement<SvgIconProps>;
  cancelFormButton?: React.ReactNode;
  buttonVariant?: "text" | "outlined" | "contained";
}> = ({
  node,
  attributes,
  hideAttributes,
  useDivider,
  fullWidthButton,
  elementNextToButton,
  buttonText,
  buttonIcon,
  cancelFormButton,
  buttonVariant,
}) => {
  switch (attributes.type) {
    case "hidden":
      return <UiNodeInputHiddenComponent node={node} attributes={attributes} />;
    case "submit":
    case "button":
      return (
        <UiNodeInputButtonComponent
          node={node}
          attributes={attributes}
          addDiveder={useDivider}
          fullWidth={fullWidthButton}
          leftElement={elementNextToButton}
          buttonText={buttonText}
          buttonIcon={buttonIcon}
          cancelFormButton={cancelFormButton}
          buttonVariant={buttonVariant}
        />
      );
    case "checkbox":
      return (
        <UiNodeInputCheckboxComponent node={node} attributes={attributes} />
      );
    default:
      return (
        <UiNodeInputDefaultComponent
          node={node}
          attributes={attributes}
          hideAttributes={hideAttributes}
        />
      );
  }
};

interface UiTextLookupSecrets extends UiText {
  context: {
    secrets: UiText[];
  };
}

function isLookupSecretText(text: UiText): text is UiTextLookupSecrets {
  return text.id === 1050015;
}

function isLookupSecretUsed(text: UiText): boolean {
  return text.id === 1050014;
}

function isTotpSecretKey(text: UiText): boolean {
  return text.id === 1050006;
}

const UiNodeTextComponent: React.FC<{
  node: UiNode;
  attributes: UiNodeTextAttributes;
  showTotpSecretKey?: boolean;
  customText?: string;
}> = ({ node, attributes, showTotpSecretKey = true, customText }) => {
  if (customText) {
    return <Typography>{customText}</Typography>;
  }

  if (isTotpSecretKey(attributes.text) && !showTotpSecretKey) {
    return null;
  }

  return (
    <>
      <Typography>{node.meta.label?.text}</Typography>
      {isLookupSecretText(attributes.text) ? (
        <Grid container spacing={2}>
          {attributes.text.context.secrets.map((secret, index) => (
            <Grid item xs={3} key={index}>
              <code>{isLookupSecretUsed(secret) ? "Used" : secret.text}</code>
            </Grid>
          ))}
        </Grid>
      ) : (
        <code>{attributes.text.text}</code>
      )}
    </>
  );
};

interface UiNodeImageProps {
  node: UiNode;
  attributes: UiNodeImageAttributes;
  style?: CSSProperties;
}

const UiNodeImageComponent: React.FC<UiNodeImageProps> = ({
  node,
  attributes,
  style,
}) => {
  return (
    <img
      src={attributes.src}
      width={attributes.width}
      height={attributes.height}
      alt={getNodeLabel(node)}
      data-id={node.meta.label?.id}
      data-testid={`node/image/${attributes.id}`}
      style={style}
    />
  );
};

interface UiNodeComponentProps {
  node: UiNode;
  imgStyle?: CSSProperties;
  showTotpSecretKey?: boolean;
  customText?: string;
  hideAttributes?: string[];
  useDivider?: boolean;
  fullWidthButton?: boolean;
  elementNextToButton?: React.ReactNode;
  buttonText?: string;
  buttonIcon?: React.ReactElement<SvgIconProps>;
  cancelFormButton?: React.ReactNode;
  buttonVariant?: "text" | "outlined" | "contained";
}

const UiNodeComponent: React.FC<UiNodeComponentProps> = ({
  node,
  imgStyle,
  showTotpSecretKey,
  customText,
  hideAttributes,
  useDivider,
  fullWidthButton,
  elementNextToButton,
  buttonText,
  buttonIcon,
  cancelFormButton,
  buttonVariant,
}) => {
  switch (node.type) {
    case "input":
      return (
        <UiNodeInputComponent
          node={node}
          attributes={node.attributes as UiNodeInputAttributes}
          hideAttributes={hideAttributes}
          useDivider={useDivider}
          fullWidthButton={fullWidthButton}
          elementNextToButton={elementNextToButton}
          buttonText={buttonText}
          buttonIcon={buttonIcon}
          cancelFormButton={cancelFormButton}
          buttonVariant={buttonVariant}
        />
      );
    case "text":
      return (
        <UiNodeTextComponent
          node={node}
          attributes={node.attributes as UiNodeTextAttributes}
          showTotpSecretKey={showTotpSecretKey}
          customText={customText}
        />
      );
    case "img":
      return (
        <UiNodeImageComponent
          node={node}
          attributes={node.attributes as UiNodeImageAttributes}
          style={imgStyle}
        />
      );
    default:
      console.error(`Unsupported node type '${node.type}'`);
      return null;
  }
};

export interface UiMessagesProps {
  messages: UiText[];
  hideMessageTypes?: ("info" | "error" | "success")[];
}

export const UiMessages: React.FC<UiMessagesProps> = ({
  messages,
  hideMessageTypes = [],
}) => {
  const { t } = useTranslation("kratos");
  return (
    <Stack spacing={2}>
      {messages.map((message, index) => {
        const translationKey = `${String(message.id)}`;
        const translatedMessage = t(translationKey);

        const translation =
          translatedMessage === translationKey
            ? message.text
            : translatedMessage;
        if (hideMessageTypes.includes(message.type)) {
          return null;
        }
        return (
          <Alert
            color={message.type}
            key={index}
            data-id={String(message.id)}
            severity={message.type}
          >
            {translation}
          </Alert>
        );
      })}
    </Stack>
  );
};

export const UiNodes: React.FC<{
  nodes: UiNode[];
  imgStyle?: CSSProperties;
  showTotpSecretKey?: boolean;
  customText?: string;
  hideAttributes?: string[];
  useDivider?: boolean;
  fullWidthButton?: boolean;
  elementNextToButton?: React.ReactNode;
  buttonText?: string;
  buttonIcon?: React.ReactElement<SvgIconProps>;
  cancelFormButton?: React.ReactNode;
  showProviders?: string[];
  buttonVariant?: "text" | "outlined" | "contained";
}> = ({
  nodes,
  imgStyle,
  showTotpSecretKey,
  customText,
  hideAttributes,
  useDivider,
  fullWidthButton,
  elementNextToButton,
  buttonText,
  buttonIcon,
  cancelFormButton,
  showProviders,
  buttonVariant,
}) => {
  const filtered = nodes.filter((node) => {
    if (node.group !== "oidc") return true;
    if (!showProviders || showProviders.length === 0) return true;

    const value = (node.attributes as any)?.value;
    return (
      typeof value === "string" &&
      showProviders.some((provider) =>
        value.toLowerCase().includes(provider.toLowerCase())
      )
    );
  });
  return (
    <>
      {filtered.map((node, index) => (
        <UiNodeComponent
          key={index}
          node={node}
          imgStyle={imgStyle}
          showTotpSecretKey={showTotpSecretKey}
          customText={customText}
          hideAttributes={hideAttributes}
          useDivider={useDivider}
          fullWidthButton={fullWidthButton}
          elementNextToButton={elementNextToButton}
          buttonText={buttonText}
          buttonIcon={buttonIcon}
          cancelFormButton={cancelFormButton}
          buttonVariant={buttonVariant}
        />
      ))}
    </>
  );
};

export interface UiFormProps<ValuesType extends FieldValues> {
  handleSubmit: (values: ValuesType) => Promise<void>;
  ui: UiContainer;
  groups?: string[];
  ignoreNodes?: string[];
  withoutDefault?: boolean;
  sx?: SxProps<Theme>;
  imgStyle?: CSSProperties;
  showTotpSecretKey?: boolean;
  customText?: string;
  hideAttributes?: string[];
  useDivider?: boolean;
  fullWidthButton?: boolean;
  elementNextToButton?: React.ReactNode;
  buttonText?: string;
  buttonIcon?: React.ReactElement<SvgIconProps>;
  cancelFormButton?: React.ReactNode;
  showProviders?: string[];
  buttonVariant?: "text" | "outlined" | "contained";
}

export const UiForm = <T extends FieldValues>({
  handleSubmit,
  ui,
  groups,
  ignoreNodes = [],
  withoutDefault = false,
  sx,
  imgStyle,
  showTotpSecretKey,
  customText,
  hideAttributes,
  useDivider,
  fullWidthButton,
  elementNextToButton,
  buttonText,
  buttonIcon,
  cancelFormButton,
  showProviders,
  buttonVariant,
  children,
}: React.PropsWithChildren<UiFormProps<T>>) => {
  const groupNodes = groups
    ? filterNodes(ui.nodes, groups, ignoreNodes)
    : ui.nodes;

  const nodes = ([] as UiNode[])
    .concat(filterNodes(ui.nodes, withoutDefault ? [] : ["default"]))
    .concat(groupNodes);

  const { initialValues, initialErrors } = getInitialFormState<T>(nodes);

  const handleFormSubmit = useMemoizedFn(
    async (values: T, e: React.BaseSyntheticEvent | undefined) => {
      await handleSubmit({
        ...values,
        ...getSubmitButtonValue(e),
      });
    }
  );

  const form = useForm<T>({
    defaultValues: initialValues,
    mode: "onChange",
  });

  useEffect(() => {
    Object.keys(initialErrors).forEach((key) => {
      form.setError(key as Path<T>, { message: initialErrors[key as keyof T] });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (groupNodes.length === 0) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)}>
        <StyledForm sx={sx}>
          {children}
          <UiNodes
            nodes={nodes}
            imgStyle={imgStyle}
            showTotpSecretKey={showTotpSecretKey}
            customText={customText}
            hideAttributes={hideAttributes}
            useDivider={useDivider}
            fullWidthButton={fullWidthButton}
            elementNextToButton={elementNextToButton}
            buttonText={buttonText}
            buttonIcon={buttonIcon}
            cancelFormButton={cancelFormButton}
            showProviders={showProviders}
            buttonVariant={buttonVariant}
          />
        </StyledForm>
      </form>
    </FormProvider>
  );
};

const StyledForm = styled(Box)(({ theme }) => ({
  display: "flex",
  flexGrow: 1,
  flexDirection: "column",
  gap: theme.spacing(4),
}));

const StyledTypography = styled(Typography)({
  fontSize: "12px",
});

/**
 * Extracts the value of the submit button pressed from the event.
 * @param event
 */
function getSubmitButtonValue(
  event: React.BaseSyntheticEvent | undefined
): Record<string, string> {
  if (
    event &&
    event.nativeEvent instanceof SubmitEvent &&
    event.nativeEvent.submitter instanceof HTMLButtonElement
  ) {
    return {
      [event.nativeEvent.submitter.name]: event.nativeEvent.submitter.value,
    };
  } else {
    return {};
  }
}
